import React from 'react';

import PropTypes from 'prop-types';
import Rating from 'react-rating';
import styled from 'styled-components';

const StarFullSVG = () => {
  return (
    <svg
      width="26"
      height="24"
      viewBox="0 0 26 24"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        d="M24.9606 8.43617L17.4398 7.34314L14.0777 0.527271C13.9859 0.340657 13.8348 0.189588 13.6482 0.0977614C13.1802 -0.133285 12.6115 0.0592537 12.3775 0.527271L9.01544 7.34314L1.49458 8.43617C1.28723 8.46579 1.09765 8.56354 0.952509 8.71165C0.777038 8.892 0.680345 9.13464 0.683677 9.38625C0.68701 9.63786 0.790095 9.87785 0.970282 10.0535L6.41172 15.3587L5.12616 22.8499C5.09601 23.0242 5.11529 23.2034 5.18182 23.3673C5.24835 23.5311 5.35946 23.6731 5.50255 23.777C5.64564 23.8809 5.81499 23.9427 5.9914 23.9553C6.1678 23.9678 6.3442 23.9307 6.50059 23.8482L13.2276 20.3114L19.9546 23.8482C20.1383 23.9459 20.3515 23.9785 20.5559 23.9429C21.0713 23.8541 21.4179 23.3653 21.329 22.8499L20.0435 15.3587L25.4849 10.0535C25.633 9.90835 25.7308 9.71878 25.7604 9.51143C25.8404 8.99305 25.479 8.51319 24.9606 8.43617V8.43617Z"
        fill="currentColor"
      />
    </svg>
  );
};

const StarEmptySVG = () => {
  return (
    <svg
      width="26"
      height="24"
      viewBox="0 0 26 24"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        d="M16.7714 7.78552L17.0041 8.25712L17.5244 8.33275L24.9404 9.41053L19.5739 14.6427L19.1975 15.0097L19.2864 15.5278L20.5538 22.9133L13.9215 19.4263L13.4561 19.1816L12.9908 19.4263L6.35842 22.9133L7.62583 15.5278L7.71475 15.0097L7.33833 14.6427L1.97181 9.41053L9.38778 8.33275L9.90816 8.25712L10.1408 7.78552L13.4561 1.06432L16.7714 7.78552ZM14.3062 0.527271C14.2144 0.340657 14.0634 0.189588 13.8767 0.0977614L14.3062 0.527271Z"
        stroke="currentColor"
        strokeWidth="2"
      />
    </svg>
  );
};

const StarFull = styled(StarFullSVG).attrs(
  ({ type = 'starFilled', size = 8 }) => ({
    type,
    size,
  })
)``;

const StarEmpty = styled(StarEmptySVG).attrs(
  ({ type = 'starUnfilled', size = 8, color, isBlackBorder, theme }) => ({
    type,
    size,
    color: isBlackBorder ? theme.colors.palette.black : color,
  })
)``;

export const Container = styled.span`
  display: inline-block;
  line-height: ${20 / 14};
  vertical-align: top;
  margin-top: -2px;
  ${(props) => (props.readonly ? 'pointer-events: none' : null)};
  svg {
    a:link & {
      /* Fix 'cursor: auto' issue caused by react-rating inline styles */
      cursor: pointer;
    }
    margin: 0 1px;
  }
`;

/**
 * Star rating component that uses [react-rating](https://github.com/dreyescat/react-rating)
 * under the hood. Any remaining props beyond those documented below are passed
 * along to its `Rating` component.
 */

const Stars = ({
  className,
  color,
  isBlackBorder,
  readonly,
  size,
  ...props
}) => {
  const starProps = { size, color };
  return (
    <Container
      className={className}
      readonly={readonly}
      data-autotag="user-stars"
    >
      <Rating
        fractions={2}
        fullSymbol={<StarFull {...starProps} />}
        emptySymbol={<StarEmpty isBlackBorder={isBlackBorder} {...starProps} />}
        readonly={readonly}
        {...props}
      />
    </Container>
  );
};

Stars.defaultProps = {
  color: 'black',
  readonly: false,
  size: 14,
};

Stars.propTypes = {
  ...Rating.propTypes,
  /**
   * Class name(s) to apply to the container element.
   */
  className: PropTypes.string,
  /**
   * Icon color to pass along to the default star icons.
   */
  color: PropTypes.string,
  isBlackBorder: PropTypes.bool,
  readonly: PropTypes.bool,

  /**
   * Icon size to pass along to the default star icons.
   */
  size: PropTypes.number,
};

export default Stars;
